import React, { useMemo, useState } from "react"
import { EditorState, Modifier, SelectionState, convertToRaw, convertFromRaw } from "draft-js"
import { Editor } from "react-draft-wysiwyg"
import { isSafeAddress } from "helpers/richTextAddress"

// The editor refuses the addresses the API blanks (the url, href and src of any entity; an
// empty one is left alone), so the text an admin saves is the text the API stores.
const ADDRESS_KEYS = ["url", "href", "src"]
const LINK_REFUSED = "Only links that start with https://, http://, mailto: or tel: are allowed."

const isUnsafe = address => address != null && address !== "" && !isSafeAddress(address)
const unsafeKeys = entity => ADDRESS_KEYS.filter(key => isUnsafe(entity.getData()[key]))

// A link added from the toolbar or pasted with a refused address loses the link and keeps
// its text; an image or an embed keeps its block with the address blanked, as the API does.
function refuseUnsafeAddresses(editorState) {
  let content = editorState.getCurrentContent()
  let refused = false
  content.getBlockMap().forEach(block => {
    block.findEntityRanges(
      character => character.getEntity() !== null && unsafeKeys(content.getEntity(character.getEntity())).length > 0,
      (start, end) => {
        refused = true
        const entityKey = block.getEntityAt(start)
        const entity = content.getEntity(entityKey)
        if (entity.getType() === "LINK") {
          const range = SelectionState.createEmpty(block.getKey()).merge({ anchorOffset: start, focusOffset: end })
          content = Modifier.applyEntity(content, range, null)
        } else {
          const blanked = {}
          unsafeKeys(entity).forEach(key => { blanked[key] = "" })
          content = content.mergeEntityData(entityKey, blanked)
        }
      },
    )
  })
  return { editorState: refused ? EditorState.set(editorState, { currentContent: content }) : editorState, refused }
}

const TextEditor = ({ defaultValue, onChange, readOnly = false, ariaLabel }) => {
  const [editorState, setEditorState] = useState(null)
  const [linkRefused, setLinkRefused] = useState(false)
  // A text saved before these addresses were refused shows such a link as its text alone, and an editor says so until the text changes.
  const initial = useMemo(() => defaultValue
    ? refuseUnsafeAddresses(EditorState.createWithContent(convertFromRaw(JSON.parse(defaultValue))))
    : { editorState: EditorState.createEmpty(), refused: false }, [defaultValue])
  const shown = editorState || initial.editorState
  return (
    <div>
      <Editor
        // react-draft-wysiwyg hands a new readOnly to Draft.js one render late, so the editor mounts again when it changes; the text
        // stays, since its state is held here.
        key={readOnly ? "read" : "write"}
        readOnly={readOnly}
        toolbarHidden={readOnly}
        ariaLabel={ariaLabel}
        editorState={shown}
        toolbarClassName="toolbarClassName"
        wrapperClassName="wrapperClassName"
        editorClassName="editorClassName"
        onEditorStateChange={state => {
          const safe = refuseUnsafeAddresses(state)
          // The message stays until the text changes again.
          if (safe.refused) setLinkRefused(true)
          else if (editorState && state.getCurrentContent() !== editorState.getCurrentContent()) setLinkRefused(false)
          setEditorState(safe.editorState)
          // A focus or a move of the cursor changes nothing the text holds.
          if (onChange && safe.editorState.getCurrentContent() !== shown.getCurrentContent()) onChange(convertToRaw(safe.editorState.getCurrentContent()))
        }}
        editorStyle={{ height: '250px' }}
      />
      {(linkRefused || (!editorState && initial.refused && !readOnly)) && <p className="text-danger" role="alert">{LINK_REFUSED}</p>}
    </div>
  )
}

export default TextEditor
